import { useState } from "react";
import { useAuth } from "@clerk/react";
import { detectPlatform, fetchReel, saveBlob, shareFiles, resLabel } from "@/lib/reels";
import { PrimaryButton, pasteFromClipboard } from "@/components/reels/controls";
import { Thumb } from "@/components/reels/BulkResults";
import { ProgressBar } from "@/components/reels/ProgressBar";

const ResultCard = ({ result, onSave, onShare }) => (
  <div className="rs-material flex gap-3 p-3 rs-fade" data-testid="single-result">
    <Thumb src={result.meta?.thumbnail} className="h-[96px] w-[64px] shrink-0" testId="single-result-thumb" />
    <div className="flex min-w-0 flex-1 flex-col justify-between">
      <div>
        <p className="line-clamp-2 text-[14px] leading-snug" data-testid="single-result-caption">{result.meta?.caption || result.name}</p>
        <p className="rs-secondary mt-1 flex gap-1.5 text-[12px]">
          {result.meta?.uploader && <span>@{result.meta.uploader}</span>}
          {resLabel(result.meta) && <span className="rs-badge" data-testid="single-result-res">{resLabel(result.meta)}</span>}
        </p>
      </div>
      <div className="mt-2 flex gap-2">
        <button className="rs-bordered text-[13px]" data-testid="single-save-again-btn" onClick={onSave}>Save Again</button>
        <button className="rs-bordered text-[13px]" data-testid="single-share-btn" onClick={onShare}>Share</button>
      </div>
    </div>
  </div>
);

export const SingleCard = ({ quality }) => {
  const { getToken, isSignedIn } = useAuth();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  const download = async () => {
    if (!detectPlatform(url)) return setStatus("Enter a valid Instagram or TikTok URL.");
    setBusy(true);
    setResult(null);
    setProgress(null);
    setStatus("");
    try {
      const item = await fetchReel(url, { quality, token: isSignedIn ? await getToken() : null, onProgress: setProgress });
      setResult(item);
      const shared = await shareFiles([item]).catch(() => false);
      if (!shared) saveBlob(item.blob, item.name);
      setStatus(shared ? "Ready. Choose “Save Video” to add it to Photos." : "Saved to Downloads.");
    } catch (e) {
      setStatus(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="rs-material flex flex-col gap-3.5 p-4 rs-fade">
        <div className="flex items-center gap-2">
          <input
            data-testid="single-url-input"
            className="rs-field min-w-0 flex-1 py-1"
            placeholder="Paste Instagram or TikTok link"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && url.trim() && download()}
            autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="url"
          />
          <button className="rs-bordered" data-testid="single-paste-btn" onClick={() => pasteFromClipboard(setUrl)}>Paste</button>
        </div>
        <PrimaryButton testId="single-download-btn" busy={busy} disabled={busy || !url.trim()} onClick={download}
          label={busy ? "Downloading…" : "Download Reel"} />
        {busy && <ProgressBar progress={progress} testId="single-progress" />}
      </div>
      {status && <p className="rs-secondary text-center text-[17px] rs-fade" data-testid="single-status">{status}</p>}
      {result && !busy && (
        <ResultCard result={result} onSave={() => saveBlob(result.blob, result.name)}
          onShare={async () => (await shareFiles([result]).catch(() => false)) || setStatus("Sharing isn't supported in this browser.")} />
      )}
    </>
  );
};
